import { Inject, Injectable } from '@angular/core';

import { NgRedux } from '@angular-redux/store';

import { RegisterWithEmailDTO, RidangoReduxAction } from '@ridango/core';

import { Observable, of } from 'rxjs';

import { NotificationFacade } from '../../../notifications';
import { Authenticator, SessionService } from '../../services';
import { CustomerAuthActions } from '../customer-auth/';
import { RegisterActions } from './register.actions';
import * as RegisterSelectors from './register.selectors';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CustomerActions } from '../../../customer';
import Env from '@ridango/core/env';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable()
export class RegisterFacade {
    registrationSteps$: Observable<string[]>;
    registrationProgress$: Observable<number>;

    registerLoaded$: Observable<boolean>;
    registerError$: Observable<any>;

    authAccessToken$: Observable<string>;

    constructor(
        @Inject('ENV') public env: Env,
        private store: NgRedux<any>,
        private service: Authenticator,
        private session: SessionService,
        private actions: RegisterActions,
        private customerAuthActions: CustomerAuthActions,
        private customerActions: CustomerActions,
        private notification: NotificationFacade,
        private translate: TranslateService,
        private router: Router
    ) {
        this.registerLoaded$ = store.select(RegisterSelectors.getRegisterLoaded);
        this.registerError$ = store.select(RegisterSelectors.getRegisterError);
        this.registrationSteps$ = store.select(RegisterSelectors.getRegistrationSteps);
        this.registrationProgress$ = store.select(RegisterSelectors.getRegistrationProgress);
        this.authAccessToken$ = store.select(RegisterSelectors.getAuthAccessToken);
    }

    // for future use
    dispatch(action: RidangoReduxAction) {
        this.store.dispatch(action);
    }

    setRegistrationProgress(progress: number) {
        this.dispatch(this.actions.setRegisterProgress(progress));
    }

    registerWithEmailAndPassword(registerDTO: RegisterWithEmailDTO) {
        this.dispatch(this.actions.registerWithCredentials());
        return this.service.signUpUserWithCredentials(registerDTO).pipe(
            map(() => {
                if (this.env.REDIRECT_TO_LOGIN_AFTER_REGISTRATION) {
                    this.router.navigate([this.translate.currentLang, 'login']).then(() => {
                        this.notification.success(
                            this.translate.instant('REGISTRATION_VALIDATION_MAIL_DESCRIPTION') + ' ' + registerDTO.email
                        );
                    });
                } else {
                    this.setRegistrationProgress(2);
                }
            }),
            catchError((e) => {
                if (this.env.WMB.AUTH) {
                    this.notification.error(this.translate.instant(e.error?.message));
                } else {
                    const msg = e.error.error_description.split(' ').join('_').toUpperCase();
                    this.notification.error(this.translate.instant(msg));
                }
                return of(e);
            })
        );
    }

    verifyAccount(token: string, isCorporate = false) {
        this.dispatch(this.actions.verifyAccount());
        return this.service.verifyAccount(token).pipe(
            switchMap((d: any) => {
                return this.service.fetchAccessToken('ridango/auth', d.access_token, d.terms_version, d.name).pipe(
                    map((data) => {
                        this.dispatch(this.actions.verifyAccountSuccess(d.access_token));
                        this.dispatch(this.customerAuthActions.loadCustomerData(data));
                        this.session.setLocalAccessToken(data, isCorporate);
                        if (this.env.SEND_CUSTOMER_LANGUAGE_TO_BACKEND) {
                            this.dispatch(this.customerActions.sendCustomerLang(this.translate.currentLang));
                        }

                        return data;
                    })
                );
            }),
            catchError(() => {
                this.dispatch(this.actions.verifyAccountError('LOCAL_TOKEN_VERIFY_FAILED'));

                return of(null);
            })
        );
    }

    verifyAccountWithoutLogin(token: string) {
        this.session.signOut();

        this.service.verifyAccount(token).subscribe(
            (d: any) => {
                const isBackendSyncVerification = /^[a-f0-9]{32}$/.test(token);
                const oauthPayload =
                    d && typeof d === 'object' && typeof d.access_token === 'string' && d.access_token.length > 0;
                if ((this.env.IS_NEW_AFC && this.env.WMB?.AUTH) || isBackendSyncVerification || !oauthPayload) {
                    this.notification.success(this.translate.instant('LOCAL_VERIFICATION_SUCCESS'), {
                        showAfterRouteChange: true
                    });
                    this.router.navigate([this.translate.currentLang, 'login']);
                } else {
                    this.service
                        .fetchAccessToken('ridango/auth', d.access_token, d.terms_version, d.name)
                        .subscribe(() => {
                            this.notification.success(this.translate.instant('LOCAL_VERIFICATION_SUCCESS'), {
                                showAfterRouteChange: true
                            });
                            this.router.navigate([this.translate.currentLang, 'login']);
                            if (this.env.SEND_CUSTOMER_LANGUAGE_TO_BACKEND) {
                                this.dispatch(this.customerActions.sendCustomerLang(this.translate.currentLang));
                            }
                        });
                }
            },
            () => {
                this.notification.error(this.translate.instant('LOCAL_TOKEN_VERIFY_FAILED'), {
                    showAfterRouteChange: true
                });
            }
        );
    }
}
